// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Link } from "react-router-dom"
import {
  BookOpen,
  Brain,
  ChevronRight,
  CreditCard,
  Lock,
  ShieldAlert,
  SlidersHorizontal,
  Target,
  User,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { useApiClient } from "@/api/useApiClient"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { constraintChipLabel } from "@/components/constraints/constraintDisplay"

/**
 * Settings hub — 2026-08-30 owner reorganization ("the list looks messed
 * up"): the flat 8-card pile became three labeled groups (About you /
 * Food & taste / App & account), each a single card of divided rows with
 * an icon and chevron — the standard settings idiom users already know
 * from iOS. Every destination is unchanged; only the structure is new.
 */

interface Row {
  to: string
  icon: LucideIcon
  titleKey: string
  subKey: string
}

const SECTIONS: Array<{ headingKey: string; rows: Row[] }> = [
  {
    headingKey: "moreProfile.hub.sectionYou",
    rows: [
      { to: "profile", icon: User, titleKey: "moreProfile.hub.profileLink", subKey: "moreProfile.hub.profileLinkSub" },
      { to: "goals", icon: Target, titleKey: "moreProfile.hub.goalsLink", subKey: "moreProfile.hub.goalsLinkSub" },
    ],
  },
  {
    headingKey: "moreProfile.hub.sectionFood",
    rows: [
      {
        to: "preferences",
        icon: SlidersHorizontal,
        titleKey: "moreProfile.hub.preferencesLink",
        subKey: "moreProfile.hub.preferencesLinkSub",
      },
      {
        to: "constraints",
        icon: ShieldAlert,
        titleKey: "moreProfile.hub.constraintsLink",
        subKey: "moreProfile.hub.constraintsLinkSub",
      },
      { to: "memory", icon: Brain, titleKey: "moreProfile.hub.memoryLink", subKey: "moreProfile.hub.memoryLinkSub" },
    ],
  },
  {
    headingKey: "moreProfile.hub.sectionApp",
    rows: [
      { to: "/app/access", icon: CreditCard, titleKey: "access.moreLink", subKey: "access.moreLinkSub" },
      { to: "/guides", icon: BookOpen, titleKey: "moreProfile.hub.guidesLink", subKey: "moreProfile.hub.guidesLinkSub" },
      { to: "privacy", icon: Lock, titleKey: "privacy.moreLink", subKey: "privacy.moreLinkSub" },
    ],
  },
]

export default function MoreHomePage() {
  const { t } = useI18n()
  const api = useApiClient()
  // 2026-09-02 approved discoverability: the constraints row shows the
  // user's CURRENT exclusions as chips (summary at a glance). Degrades
  // silently to the static subtitle — never a blocked hub.
  const [exclusionChips, setExclusionChips] = useState<{ type: string; subjectId: string }[]>([])
  useEffect(() => {
    let cancelled = false
    api
      .getHardConstraints()
      .then(res => {
        if (cancelled) return
        setExclusionChips(
          res.constraints
            .filter(c => c.type === "allergen" || c.type === "never_eat")
            .map(({ type, subjectId }) => ({ type, subjectId }))
            .slice(0, 5),
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [api])
  const { email, signOut } = useAuth()

  return (
    <section aria-labelledby="more-heading" className="flex flex-col gap-6">
      <div>
        <h1 id="more-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
          {t("more.title")}
        </h1>
        {email && <p className="mt-1 text-sm text-ink-2">{t("today.signedInAs", { email })}</p>}
      </div>

      <nav aria-label={t("more.title")} className="flex flex-col gap-5">
        {SECTIONS.map(section => (
          <div key={section.headingKey} className="flex flex-col gap-2">
            <h2 className="px-1 text-xs font-bold tracking-wide text-ink-2 uppercase">
              {t(section.headingKey)}
            </h2>
            <div className="divide-y divide-line overflow-hidden rounded-bb-xl border border-line bg-paper">
              {section.rows.map(({ to, icon: Icon, titleKey, subKey }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3.5 px-4 py-3.5 transition-colors hover:bg-cream"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-bb-md bg-green/10">
                    <Icon className="size-4.5 text-green" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-ink">{t(titleKey)}</span>
                    {to === "constraints" && exclusionChips.length > 0 ? (
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {exclusionChips.map(chip => (
                          <span
                            key={`${chip.type}-${chip.subjectId}`}
                            className="rounded-full border border-line bg-cream px-2 py-0.5 text-xs text-ink"
                          >
                            {constraintChipLabel(chip.type, chip.subjectId, t)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="block truncate text-sm text-ink-2">{t(subKey)}</span>
                    )}
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <Button
        type="button"
        variant="outline"
        className="min-h-11 self-start"
        onClick={() => void signOut()}
      >
        {t("nav.signOut")}
      </Button>
    </section>
  )
}
